import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Check, Bot } from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import { useChatbotContext } from '../../../context/ChatbotContext';
import { useNavigate } from 'react-router-dom';

const ThreadList = ({ chatbotId, onSelectThread, selectedThreadId, filter, dateRange }) => {
    const [threads, setThreads] = useState([]);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { user } = useUser();
    const { chatbots } = useChatbotContext();
    const navigate = useNavigate();

    useEffect(() => {
        // Define fetch inside effect or use useCallback (moving inside is easier for now)
        const fetchThreads = async () => {
            try {
                const url = chatbotId ? `/api/threads/${chatbotId}` : `/api/threads`;

                // Get token if user exists
                const token = user ? await user.getIdToken() : null;

                const config = {
                    params: {
                        status: filter !== 'all' ? filter : undefined,
                        startDate: dateRange?.startDate,
                        endDate: dateRange?.endDate
                    },
                    headers: {}
                };

                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`;
                }

                const response = await axios.get(url, config);
                setThreads(response.data.threads);
            } catch (err) {
                console.error("Failed to fetch threads", err);
            }
        };

        if (user) {
            fetchThreads();
        }

        // Set up a poller for real-time updates (MVP style)
        const interval = setInterval(() => {
            if (user) fetchThreads();
        }, 10000);

        return () => clearInterval(interval);
    }, [chatbotId, filter, user, dateRange]);

    // Handle delete - first click shows confirm, second click deletes
    const handleDeleteClick = async (e, threadId) => {
        e.stopPropagation(); // Prevent thread selection

        if (deleteConfirmId === threadId) {
            // Already in confirm mode, proceed with delete
            setIsDeleting(true);
            try {
                const token = user ? await user.getIdToken() : null;
                await axios.delete(`/api/threads/${threadId}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });

                // Remove from local state
                setThreads(prev => prev.filter(t => t.thread_id !== threadId));

                // If this was the selected thread, clear selection
                if (selectedThreadId === threadId) {
                    onSelectThread(null);
                }
            } catch (err) {
                console.error("Failed to delete thread", err);
            } finally {
                setIsDeleting(false);
                setDeleteConfirmId(null);
            }
        } else {
            // First click - enter confirm mode
            setDeleteConfirmId(threadId);
            // Auto-reset after 3 seconds if not confirmed
            setTimeout(() => {
                setDeleteConfirmId(prev => prev === threadId ? null : prev);
            }, 3000);
        }
    };

    return (
        <div className="h-full overflow-y-auto custom-scrollbar">
            {threads.map(thread => (
                <div
                    key={thread.thread_id}
                    className={`
                        group w-full p-4 border-b border-border cursor-pointer transition-all duration-200
                        hover:bg-accent/40
                        ${selectedThreadId === thread.thread_id ? 'bg-accent/50 border-l-[3px] border-l-primary pl-[13px]' : 'pl-4 border-l-transparent border-l-[3px]'}
                    `}
                    onClick={() => {
                        if (chatbotId) {
                            navigate(`/chatbot/${chatbotId}/inbox/thread/${thread.thread_id}`);
                        } else {
                            navigate(`/inbox/thread/${thread.thread_id}`);
                        }
                        onSelectThread(thread);
                    }}
                >
                    <div className="flex items-start gap-3">
                        <div className="relative">
                            <div className={`
                                w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 shadow-sm
                                ${selectedThreadId === thread.thread_id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                            `}>
                                {thread.customer_email ? thread.customer_email[0].toUpperCase() : 'V'}
                            </div>

                            {/* Human Handling / Status Indicator (Top Right) */}
                            <span className={`
                                absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background z-10
                                ${thread.human_handling ? 'bg-orange-500 animate-pulse' : (thread.status === 'open' ? 'bg-green-500' : 'bg-gray-400')}
                            `}></span>

                            {/* Source Chatbot Indicator (Bottom Right) */}
                            {(() => {
                                const relatedChatbot = chatbots?.find(c => c.chatbot_id === thread.chatbot_id);
                                if (!chatbotId && relatedChatbot) {
                                    return (
                                        <div
                                            className="absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 border-2 border-background flex items-center justify-center overflow-hidden shadow-sm z-10"
                                            title={relatedChatbot.config?.assistantName || "Chatbot"}
                                        >
                                            {relatedChatbot.config?.botIconUrl ? (
                                                <img src={relatedChatbot.config.botIconUrl} alt="bot" className="w-full h-full object-cover" />
                                            ) : (
                                                <Bot size={10} className="text-white" />
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-0.5">
                                <span className={`text-sm truncate pr-2 ${selectedThreadId === thread.thread_id ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                                    {thread.customer_email
                                        ? thread.customer_email.split('@')[0]
                                        : `Visitor ${thread.thread_id.replace('thread_', '').substring(0, 4)}`}
                                </span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <span className="text-[11px] text-muted-foreground tabular-nums">
                                        {new Date(thread.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    {/* Delete Button */}
                                    <button
                                        onClick={(e) => handleDeleteClick(e, thread.thread_id)}
                                        disabled={isDeleting}
                                        className={`
                                            p-1 rounded transition-all duration-200
                                            ${deleteConfirmId === thread.thread_id
                                                ? 'opacity-100 bg-red-100 text-red-600 hover:bg-red-200'
                                                : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 hover:bg-red-50'}
                                            disabled:opacity-50 disabled:cursor-not-allowed
                                        `}
                                        title={deleteConfirmId === thread.thread_id ? 'Click to confirm delete' : 'Delete conversation'}
                                    >
                                        {deleteConfirmId === thread.thread_id ? (
                                            <Check size={14} strokeWidth={2.5} />
                                        ) : (
                                            <Trash2 size={14} />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground truncate max-w-[140px] leading-relaxed">
                                    {thread.last_message_preview || 'No messages yet'}
                                </p>
                                {thread.assigned_to && (
                                    <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                                        @{thread.assigned_to}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            {threads.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <p className="text-sm">No conversations found.</p>
                </div>
            )}
        </div>
    );
};

export default ThreadList;
